import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { CampaignStatusBadge } from "@/components/campaigns/CampaignStatusBadge";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Button } from "@/components/ui/button";
import { useCampaignStore } from "@/store/campaignStore";
import { EmailCampaignStatus, EmailCampaignType } from "@/types/email";
import type { EmailCampaign } from "@/types/email";

type StatusFilter = "All" | EmailCampaignStatus;

const STATUS_FILTERS: StatusFilter[] = [
  "All",
  EmailCampaignStatus.Draft,
  EmailCampaignStatus.Scheduled,
  EmailCampaignStatus.Sending,
  EmailCampaignStatus.Sent,
  EmailCampaignStatus.Failed,
];

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: diffDays > 365 ? "numeric" : undefined,
  });
}

function formatScheduledDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function getTypeLabel(type: EmailCampaignType): string {
  switch (type) {
    case EmailCampaignType.Promotional:
      return "Promotional";
    case EmailCampaignType.Transactional:
      return "Transactional";
    case EmailCampaignType.Newsletter:
      return "Newsletter";
    default:
      return type;
  }
}

export default function EmailCampaignsPage() {
  const navigate = useNavigate();
  const { campaigns, isLoading, error, fetchCampaigns, deleteCampaign, isSaving } = useCampaignStore();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("All");
  const [deleteTarget, setDeleteTarget] = useState<EmailCampaign | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 12;

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  const filtered = statusFilter === "All"
    ? campaigns
    : campaigns.filter((c) => c.status === statusFilter);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await deleteCampaign(deleteTarget.id);
    setDeleteTarget(null);
  };

  return (
    <ProtectedRoute>
      <div className="lw-campaign-list container mx-auto px-4 py-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="lw-campaign-list-header flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Campaigns</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                Manage and send your email campaigns
              </p>
            </div>
            <Button onClick={() => navigate("/campaigns/new")} className="flex items-center gap-2">
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              New Campaign
            </Button>
          </div>

          {/* Status Filters */}
          {!isLoading && !error && campaigns.length > 0 && (
            <div className="lw-campaign-filters flex gap-2 flex-wrap mb-6">
              {STATUS_FILTERS.map((s) => {
                const count = s === "All" ? campaigns.length : campaigns.filter((c) => c.status === s).length;
                if (s !== "All" && count === 0) return null;
                return (
                  <button
                    key={s}
                    onClick={() => { setStatusFilter(s); setCurrentPage(1); }}
                    className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                      statusFilter === s
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                    }`}
                  >
                    {s} {count > 0 && <span className="ml-1 opacity-70">{count}</span>}
                  </button>
                );
              })}
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="lw-campaign-error rounded-lg border border-destructive/50 bg-destructive/10 p-4 mb-6">
              <div className="flex items-center gap-2">
                <svg className="h-5 w-5 text-destructive" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-destructive">{error}</p>
              </div>
            </div>
          )}

          {/* Loading */}
          {isLoading && (
            <div className="lw-campaign-loading flex items-center justify-center py-16">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-border border-t-primary" />
                Loading…
              </div>
            </div>
          )}

          {/* Empty State */}
          {!isLoading && !error && campaigns.length === 0 && (
            <div className="lw-campaign-empty rounded-lg border border-dashed border-border p-16 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                <svg className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="mt-4 text-base font-semibold text-foreground">No campaigns yet</h3>
              <p className="mt-1.5 text-sm text-muted-foreground max-w-xs mx-auto">
                Create your first campaign to start sending emails to your audience.
              </p>
              <button
                onClick={() => navigate("/campaigns/new")}
                className="mt-5 inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                New Campaign
              </button>
            </div>
          )}

          {/* Empty filtered state */}
          {!isLoading && !error && campaigns.length > 0 && filtered.length === 0 && (
            <div className="lw-campaign-empty-filtered rounded-lg border border-dashed border-border p-12 text-center">
              <p className="text-sm text-muted-foreground">No {statusFilter.toLowerCase()} campaigns.</p>
            </div>
          )}

          {/* Campaign Grid */}
          {!isLoading && !error && paginated.length > 0 && (
            <>
              <div className="lw-campaign-grid grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {paginated.map((campaign) => (
                  <CampaignCard
                    key={campaign.id}
                    campaign={campaign}
                    onDelete={() => setDeleteTarget(campaign)}
                  />
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="lw-campaign-pagination mt-8 flex items-center justify-center gap-2">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="rounded-md border border-border bg-background px-3 py-2 text-sm font-medium text-foreground hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <span className="text-sm text-muted-foreground">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="rounded-md border border-border bg-background px-3 py-2 text-sm font-medium text-foreground hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        onConfirm={handleDelete}
        title="Delete Campaign"
        description={`Are you sure you want to delete "${deleteTarget?.name}"? This action cannot be undone.`}
        confirmLabel={isSaving ? "Deleting…" : "Delete"}
        variant="destructive"
      />
    </ProtectedRoute>
  );
}

interface CampaignCardProps {
  campaign: EmailCampaign;
  onDelete: () => void;
}

function CampaignCard({ campaign, onDelete }: CampaignCardProps) {
  const isDraft = campaign.status === EmailCampaignStatus.Draft;

  return (
    <div className="lw-campaign-card group flex flex-col rounded-lg border border-border bg-card shadow-sm transition-all duration-200 hover:shadow-md hover:border-primary/40 hover:-translate-y-0.5 overflow-hidden">
      {/* Card header with status */}
      <div className="flex items-start justify-between p-4 pb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <CampaignStatusBadge status={campaign.status} />
            <span className="text-[10px] text-muted-foreground">{getTypeLabel(campaign.type)}</span>
          </div>
          <h3 className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-1">
            {campaign.name}
          </h3>
        </div>
      </div>

      {/* Subject line */}
      <div className="px-4 pb-3">
        <p className="text-xs text-muted-foreground line-clamp-1">
          <span className="font-medium text-foreground/70">Subject: </span>
          {campaign.subject || <span className="italic opacity-60">No subject</span>}
        </p>
        {campaign.fromName && (
          <p className="mt-0.5 text-xs text-muted-foreground line-clamp-1">
            <span className="font-medium text-foreground/70">From: </span>
            {campaign.fromName}
          </p>
        )}
      </div>

      {/* Stats row */}
      <div className="mx-4 flex items-center gap-3 rounded-md bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
        <div className="flex items-center gap-1">
          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <span>{campaign.estimatedRecipients.toLocaleString()} recipients</span>
        </div>
        {campaign.status === EmailCampaignStatus.Sent && campaign.openCount > 0 && (
          <div className="flex items-center gap-1">
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            <span>
              {campaign.actualRecipientsSent > 0
                ? `${Math.round((campaign.openCount / campaign.actualRecipientsSent) * 100)}% opens`
                : `${campaign.openCount} opens`}
            </span>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-4 py-3 mt-2 border-t border-border">
        <span className="text-[11px] text-muted-foreground">
          {campaign.status === EmailCampaignStatus.Sent && campaign.sentAt
            ? `Sent ${formatDate(campaign.sentAt)}`
            : campaign.status === EmailCampaignStatus.Scheduled && campaign.scheduledSendAt
            ? `Scheduled ${formatScheduledDate(campaign.scheduledSendAt)}`
            : `Created ${formatDate(campaign.createdAt)}`}
        </span>
        <div className="flex items-center gap-2">
          {isDraft && (
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDelete(); }}
              className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
              title="Delete campaign"
            >
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          )}
          <Link
            to={`/campaigns/${campaign.id}`}
            className="text-xs font-medium text-primary hover:underline"
          >
            {isDraft ? "Edit →" : "View →"}
          </Link>
        </div>
      </div>
    </div>
  );
}
